import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController, NavController, PopoverController } from '@ionic/angular';
import { AccountPaymentFavoriteService } from '@wlp/ui-bs-account-payment-favorite';
import { LotWithBilletCompletedDto } from '@wlp/ui-px-cashin';
import { BilletLotService, ChargingService } from '@wlp/ui-bs-cashin';
import { UserInfoService } from '@wlp/ui-bs-login';
import { UiBsTranslateService } from '@wlp/ui-bs-translate';
import * as moment from 'moment';
import { LayoutConfigModel } from 'src/app/core/layout/models/layout-config.model';
import { LayoutConfigService } from 'src/app/core/layout/services/layout-config.service';
import { FavoritesDataFilterPipe } from '../../favorites-data-filter.pipe';
import { LoadingService } from 'src/app/core/layout/services/loading.service';
import { ModalAlertComponent } from 'src/app/views/themes/sentinel/modals/modal-alert/modal-alert.component';
import { ModalBilletAlertCancelComponent } from 'src/app/views/themes/sentinel/modals/modal-billet-alert-cancel/modal-billet-alert-cancel';
import { ModalTokenComponent } from 'src/app/views/themes/sentinel/modals/modal-token/modal-token.component';

interface IBilletReviewRouteParams {
  uuidAccountPayment: string;
  uuidLot: string;
}

@Component({
  selector: 'ui-t-billet-list',
  templateUrl: './billet-list.page.html',
  styleUrls: ['./billet-list.page.scss'],
  providers: [FavoritesDataFilterPipe],
})
export class CharginBilletList implements OnInit {
  lot: LotWithBilletCompletedDto

  public errorMessages: any;
  private langDefault: string;
  private layoutConfigValue: LayoutConfigModel;
  private readonly OPERATION: string = '1002';
  routeParams: IBilletReviewRouteParams;
  
  constructor(
    public modalController: ModalController,
    public billetLotService: BilletLotService,
    public service: ChargingService,
    protected userInfoService: UserInfoService,
    private navCtrl: NavController,
    public accountPaymentFavoriteService: AccountPaymentFavoriteService,
    public router: Router,
    public popoverController: PopoverController,
    public pipeFilter: FavoritesDataFilterPipe,
    private route: ActivatedRoute,
    private layoutConfigService: LayoutConfigService,
    private translate: UiBsTranslateService,
    private loadingService: LoadingService,
  ) {
    moment.locale('pt-br');
  }

  ngOnInit(): void {

    this.routeParams = {
      uuidAccountPayment: this.route.snapshot.paramMap.get('uuidAccountPayment'),
      uuidLot: this.route.snapshot.paramMap.get('uuidLot')
    };
    
    this.setLanguage();
    this.getLot();
  }

  setLanguage() {
    this.layoutConfigValue = this.layoutConfigService.getSavedConfig();
    this.langDefault = this.layoutConfigValue.wlTheme.languageDefault;
    this.translate.setDefaultLang(this.langDefault);
  }

  getLot() {
    const { uuidAccountPayment, uuidLot } = this.routeParams;
    this.billetLotService.getLotWithBilletsByLotUuid(uuidAccountPayment, uuidLot)
      .subscribe(
        (lot) => {
          this.loadingService.show();
          // @ts-ignore
          if (lot.statusCode && lot.statusCode !== 200) {
            this.statusNotification('warning-circle', 'CASH_OUT.BILLET_LIST_PAGE.BILLET_LIST_TABLE.BILLET_LIST_MODAL.FIND_ERROR', true)
          }
          this.lot = lot as LotWithBilletCompletedDto;
        },
        (error) => {
          this.statusNotification('warning-circle', 'CASH_OUT.BILLET_LIST_PAGE.BILLET_LIST_TABLE.BILLET_LIST_MODAL.FIND_ERROR', true)
        },
        () => {
          this.loadingService.hide()
        }
      )
  }

  handleClickSubmitForm() {
    this.validateToken();
  }

  public async validateToken(_confirmation?: boolean) {
    const modalToken = await this.modalController.create({
      component: ModalTokenComponent,
      cssClass: 'modal-token',
      componentProps: {
        validationOnly: true,
      },
      backdropDismiss: false,
    });

    modalToken.onDidDismiss().then((data) => {
      if (data['data'] && data['role']) {
        this.emitLot(data.role)
      }
    });

    return await modalToken.present();
  }

  async emitLot(token: string) {
    const {uuidAccountPayment, uuidLot} = this.routeParams;
    const body = {
      uuidLotBillet: uuidLot,
      uuidAccountPayment: uuidAccountPayment,
      token,
    }
    this.loadingService.show();
    this.billetLotService.emitLot(body)
      .subscribe(
        response => {
          this.loadingService.hide();
          this.goToLotDetail();
        },
        error => {
          console.error('# emitLot error: ', error);
          this.statusNotification('warning-circle', 'CASH_IN.ACCOUNT_RECHARGE.MESSAGES.FAILED', true);
        })
  }

  async handleClickCancel() {
    const modalToken = await this.modalController.create({
      component: ModalBilletAlertCancelComponent,
      cssClass: 'modal-billet-alert-cancel',
      componentProps: {
        validationOnly: true,
        codeOperation: this.OPERATION,
        confirmation: true,
        confirm: () => this.goToLotDetail(),
        description: 'CASH_IN.ACCOUNT_RECHARGE.CONFIRMATION.MODAL.CANCEL.TITLE',
        alert2: 'CASH_IN.ACCOUNT_RECHARGE.CONFIRMATION.MODAL.CANCEL.TEXT',
        confirmBtn: 'CASH_IN.ACCOUNT_RECHARGE.CONFIRMATION.MODAL.CANCEL.BUTTON_CONFIRM',
        cancelBtn: 'CASH_IN.ACCOUNT_RECHARGE.CONFIRMATION.MODAL.CANCEL.BUTTON_CANCEL'
      },
      backdropDismiss: false,
    });
      
    return await modalToken.present();
  }

  goToLotDetail() {
    const { uuidAccountPayment, uuidLot } = this.routeParams;
    this.navCtrl.navigateRoot([`app/charging/billet/v2/lot/${uuidAccountPayment}/${uuidLot}`]);
  }

  goBack() {
    this.navCtrl.navigateRoot('/app/dashboard');
  }

  private async statusNotification(status: string, message: string, translateFlag: boolean) {
    this.loadingService.hide();
    const translate = translateFlag ? true : false;
    const modalStatus = await this.modalController.create({
      component: ModalAlertComponent,
      cssClass: 'modal-alert-class',
      componentProps: {
        status: status,
        message: message,
        confirmBtn:'CASH_OUT.ACCOUNT_CHARGING.CLOSE_BUTTON',
        translate: translate,
        hasActionAfterConfirmation: true,
      },
      backdropDismiss: false,
    });
    return await modalStatus.present();
  }

}
